import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { api } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';
import { ASSET_CATEGORIES, getCategoryImage } from '../utils/helpers';
import { ArrowLeft, Sparkles, Loader2, CheckCircle, AlertCircle, Upload, Camera, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { InsuranceOCRReview } from '../components/InsuranceOCRReview';

const AiSuggestedChip = () => (
  <span
    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-purple-500/15 text-purple-300 border border-purple-500/30 w-fit"
    data-testid="ai-suggested-chip"
  >
    <Sparkles size={9} />
    AI-suggested
  </span>
);

const labelWithChip = "text-sm uppercase tracking-wider text-muted-foreground flex flex-col items-start gap-1";

export default function AssetForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatCurrency, getCurrencySymbol } = useCurrency();
  const isEditing = Boolean(id);
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [fetchingAsset, setFetchingAsset] = useState(isEditing);
  const [valuating, setValuating] = useState(false);
  const [valuation, setValuation] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [aiSuggestedFields, setAiSuggestedFields] = useState({}); // {fieldName: true} — clears on manual edit
  const [form, setForm] = useState({
    name: '',
    category: '',
    description: '',
    purchase_price: '',
    current_value: '',
    purchase_date: '',
    location: '',
    image_url: '',
    insurance_provider: '',
    insurance_policy: '',
    insurance_expiry: '',
    insured_value: '',
    notes: '',
    condition: '',
    year: '',
    brand: ''
  });

  useEffect(() => {
    if (isEditing) {
      fetchAsset();
    }
  }, [id]);

  const fetchAsset = async () => {
    try {
      const response = await api.getAsset(id);
      const asset = response.data;
      setForm({
        name: asset.name || '',
        category: asset.category || '',
        description: asset.description || '',
        purchase_price: asset.purchase_price?.toString() || '',
        current_value: asset.current_value?.toString() || '',
        purchase_date: asset.purchase_date || '',
        location: asset.location || '',
        image_url: asset.image_url || '',
        insurance_provider: asset.insurance_provider || '',
        insurance_policy: asset.insurance_policy || '',
        insurance_expiry: asset.insurance_expiry || '',
        insured_value: asset.insured_value != null ? String(asset.insured_value) : '',
        notes: asset.notes || '',
        condition: asset.condition || '',
        year: asset.year || '',
        brand: asset.brand || ''
      });
      if (asset.image_url) {
        setImagePreview(asset.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${asset.image_url}` : asset.image_url);
      }
    } catch (error) {
      toast.error('Failed to load asset');
      navigate('/assets');
    } finally {
      setFetchingAsset(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // Clear AI-suggested chip when user manually edits the field
    if (aiSuggestedFields[field]) {
      setAiSuggestedFields(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    // Clear valuation if key fields change
    if (['name', 'category', 'description', 'condition'].includes(field)) {
      setValuation(null);
    }
  };

  const handleInsuranceOCRApply = (patch, aiKeys) => {
    setForm(prev => ({ ...prev, ...patch }));
    setAiSuggestedFields(prev => {
      const next = { ...prev };
      aiKeys.forEach(k => { next[k] = true; });
      return next;
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, and WebP images are allowed');
      return;
    }

    // Validate size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);

    setUploadingImage(true);
    try {
      const response = await api.uploadImage(file);
      const uploadedUrl = response.data.url;
      handleChange('image_url', uploadedUrl);
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
      setImagePreview(null);
      console.error('Upload error:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    handleChange('image_url', '');
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAIValuation = async () => {
    if (!form.name || !form.category) {
      toast.error('Please enter asset name and category first');
      return;
    }

    setValuating(true);
    setValuation(null);

    try {
      const response = await api.getAIValuation({
        name: form.name,
        category: form.category,
        description: form.description || null,
        condition: form.condition || null,
        year: form.year || null,
        brand: form.brand || null,
        provenance: form.location || null
      });

      setValuation(response.data);
      
      // Auto-fill current value with AI estimate
      handleChange('current_value', response.data.estimated_value.toString());
      
      toast.success('AI valuation complete');
    } catch (error) {
      toast.error('Failed to get AI valuation');
      console.error('AI Valuation error:', error);
    } finally {
      setValuating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.category || !form.purchase_price || !form.current_value || !form.purchase_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const data = {
        name: form.name,
        category: form.category,
        description: form.description || null,
        purchase_price: parseFloat(form.purchase_price),
        current_value: parseFloat(form.current_value),
        purchase_date: form.purchase_date,
        location: form.location || null,
        image_url: form.image_url || getCategoryImage(form.category),
        insurance_provider: form.insurance_provider || null,
        insurance_policy: form.insurance_policy || null,
        insurance_expiry: form.insurance_expiry || null,
        insured_value: form.insured_value !== '' ? parseFloat(form.insured_value) : null,
        notes: form.notes || null
      };

      if (isEditing) {
        await api.updateAsset(id, data);
        toast.success('Asset updated');
      } else {
        await api.createAsset(data);
        toast.success('Asset added to your vault');
      }
      navigate('/assets');
    } catch (error) {
      toast.error(isEditing ? 'Failed to update asset' : 'Failed to add asset');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingAsset) {
    return (
      <DashboardLayout>
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="asset-form-page">
        {/* Back Button */}
        <Link 
          to="/assets" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-6 transition-colors"
          data-testid="back-to-assets"
        >
          <ArrowLeft size={18} />
          Back to Assets
        </Link>

        <div className="luxury-card p-8">
          <h1 className="font-serif text-3xl text-white mb-2">
            {isEditing ? 'Edit Asset' : 'Add New Asset'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {isEditing ? 'Update your asset details' : 'Add a new luxury asset to your vault'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Info */}
            <div className="space-y-4">
              <h2 className="text-gold uppercase tracking-wider text-xs font-semibold">Basic Information</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                    Asset Name *
                  </Label>
                  <Input
                    value={form.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="e.g., Hermès Birkin 25 Togo Black"
                    className="luxury-input"
                    data-testid="asset-name-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                    Category *
                  </Label>
                  <Select value={form.category} onValueChange={(value) => handleChange('category', value)}>
                    <SelectTrigger className="luxury-input" data-testid="asset-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0F0F] border-[#262626]">
                      {ASSET_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                    Brand/Maker
                  </Label>
                  <Input
                    value={form.brand}
                    onChange={(e) => handleChange('brand', e.target.value)}
                    placeholder="e.g., Hermès, Ferrari"
                    className="luxury-input"
                    data-testid="asset-brand-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                    Year/Vintage
                  </Label>
                  <Input
                    value={form.year}
                    onChange={(e) => handleChange('year', e.target.value)}
                    placeholder="e.g., 2023, 1982"
                    className="luxury-input"
                    data-testid="asset-year-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                    Condition
                  </Label>
                  <Select value={form.condition} onValueChange={(value) => handleChange('condition', value)}>
                    <SelectTrigger className="luxury-input" data-testid="asset-condition-select">
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0F0F] border-[#262626]">
                      <SelectItem value="excellent">Excellent / Like New</SelectItem>
                      <SelectItem value="very_good">Very Good</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                  Description
                </Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Include details like materials, size, provenance, special features..."
                  className="luxury-input min-h-[100px]"
                  data-testid="asset-description-input"
                />
              </div>
            </div>

            {/* Valuation */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-gold uppercase tracking-wider text-xs font-semibold">Valuation</h2>
                <Button
                  type="button"
                  onClick={handleAIValuation}
                  disabled={valuating || !form.name || !form.category}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-sm px-4 py-2 h-auto"
                  data-testid="ai-valuation-btn"
                >
                  {valuating ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Analysing...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} className="mr-2" />
                      AI Valuation
                    </>
                  )}
                </Button>
              </div>

              {/* AI Valuation Result */}
              {valuation && (
                <div className="p-4 bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-lg" data-testid="ai-valuation-result">
                  <div className="flex items-start gap-3 mb-4">
                    <CheckCircle size={20} className="text-green-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-white font-medium">AI Market Valuation</p>
                      <p className="text-sm text-muted-foreground">{valuation.reasoning}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      valuation.confidence === 'high' ? 'bg-green-500/20 text-green-400' :
                      valuation.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {valuation.confidence} confidence
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-black/30 rounded">
                      <p className="text-xs text-muted-foreground mb-1">Low Estimate</p>
                      <p className="text-lg text-white">{formatCurrency(valuation.value_range_low)}</p>
                    </div>
                    <div className="text-center p-3 bg-gold/10 border border-gold/30 rounded">
                      <p className="text-xs text-gold mb-1">Estimated Value</p>
                      <p className="text-xl text-gold font-semibold">{formatCurrency(valuation.estimated_value)}</p>
                    </div>
                    <div className="text-center p-3 bg-black/30 rounded">
                      <p className="text-xs text-muted-foreground mb-1">High Estimate</p>
                      <p className="text-lg text-white">{formatCurrency(valuation.value_range_high)}</p>
                    </div>
                  </div>

                  {valuation.market_factors && valuation.market_factors.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {valuation.market_factors.map((factor, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-[#1A1A1A] text-muted-foreground rounded">
                          {factor}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                    Purchase Price ($) *
                  </Label>
                  <Input
                    type="number"
                    value={form.purchase_price}
                    onChange={(e) => handleChange('purchase_price', e.target.value)}
                    placeholder="0.00"
                    className="luxury-input"
                    data-testid="asset-purchase-price-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    Current Value ($) *
                    {valuation && <Sparkles size={12} className="text-purple-400" />}
                  </Label>
                  <Input
                    type="number"
                    value={form.current_value}
                    onChange={(e) => handleChange('current_value', e.target.value)}
                    placeholder="0.00"
                    className={`luxury-input ${valuation ? 'border-purple-500/50' : ''}`}
                    data-testid="asset-current-value-input"
                    required
                  />
                  {!valuation && !form.current_value && form.name && form.category && (
                    <p className="text-xs text-purple-400 flex items-center gap-1">
                      <Sparkles size={10} />
                      Click "AI Valuation" to auto-estimate
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                    Purchase Date *
                  </Label>
                  <Input
                    type="date"
                    value={form.purchase_date}
                    onChange={(e) => handleChange('purchase_date', e.target.value)}
                    className="luxury-input"
                    data-testid="asset-purchase-date-input"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Location & Image */}
            <div className="space-y-4">
              <h2 className="text-gold uppercase tracking-wider text-xs font-semibold">Details</h2>
              
              <div className="space-y-2">
                <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                  Location
                </Label>
                <Input
                  value={form.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="e.g., Monaco, Private Collection"
                  className="luxury-input"
                  data-testid="asset-location-input"
                />
              </div>

              {/* Image Upload */}
              <div className="space-y-2">
                <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                  Asset Image
                </Label>

                {/* Preview */}
                {(imagePreview || form.image_url) && (
                  <div className="relative w-full max-w-xs" data-testid="image-preview-container">
                    <img
                      src={imagePreview || (form.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${form.image_url}` : form.image_url)}
                      alt="Asset preview"
                      className="w-full h-48 object-cover rounded border border-[#262626]"
                      data-testid="image-preview"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 w-7 h-7 bg-black/70 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      data-testid="remove-image-btn"
                    >
                      <X size={14} className="text-white" />
                    </button>
                  </div>
                )}

                {/* Upload area */}
                {!imagePreview && !form.image_url && (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-[#262626] rounded-lg p-8 text-center cursor-pointer hover:border-gold/50 transition-colors"
                    data-testid="image-upload-area"
                  >
                    {uploadingImage ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 size={32} className="text-gold animate-spin" />
                        <p className="text-sm text-muted-foreground">Uploading...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                          <Camera size={24} className="text-muted-foreground" />
                        </div>
                        <p className="text-sm text-white">Click to upload a photo</p>
                        <p className="text-xs text-muted-foreground">JPEG, PNG, WebP up to 5MB</p>
                      </div>
                    )}
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="image-file-input"
                />

                {/* URL fallback */}
                {!imagePreview && !form.image_url && (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1 h-px bg-[#262626]" />
                    <span className="text-xs text-muted-foreground">or paste a URL</span>
                    <div className="flex-1 h-px bg-[#262626]" />
                  </div>
                )}
                {!imagePreview && !form.image_url && (
                  <Input
                    value={form.image_url}
                    onChange={(e) => handleChange('image_url', e.target.value)}
                    placeholder="https://..."
                    className="luxury-input"
                    data-testid="asset-image-url-input"
                  />
                )}
              </div>
            </div>

            {/* Insurance */}
            <div className="space-y-4">
              <h2 className="text-gold uppercase tracking-wider text-xs font-semibold">Insurance</h2>

              {/* AI Document Scanner — review-before-save */}
              <InsuranceOCRReview
                onApply={handleInsuranceOCRApply}
                currentInsuredValue={form.insured_value}
              />
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className={labelWithChip}>
                    <span>Provider</span>
                    {aiSuggestedFields.insurance_provider && <AiSuggestedChip />}
                  </Label>
                  <Input
                    value={form.insurance_provider}
                    onChange={(e) => handleChange('insurance_provider', e.target.value)}
                    placeholder="e.g., AXA Art"
                    className={`luxury-input ${aiSuggestedFields.insurance_provider ? 'border-purple-500/50' : ''}`}
                    data-testid="asset-insurance-provider-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className={labelWithChip}>
                    <span>Policy Number</span>
                    {aiSuggestedFields.insurance_policy && <AiSuggestedChip />}
                  </Label>
                  <Input
                    value={form.insurance_policy}
                    onChange={(e) => handleChange('insurance_policy', e.target.value)}
                    placeholder="POL-123456"
                    className={`luxury-input ${aiSuggestedFields.insurance_policy ? 'border-purple-500/50' : ''}`}
                    data-testid="asset-insurance-policy-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className={labelWithChip}>
                    <span>Expiry Date</span>
                    {aiSuggestedFields.insurance_expiry && <AiSuggestedChip />}
                  </Label>
                  <Input
                    type="date"
                    value={form.insurance_expiry}
                    onChange={(e) => handleChange('insurance_expiry', e.target.value)}
                    className={`luxury-input ${aiSuggestedFields.insurance_expiry ? 'border-purple-500/50' : ''}`}
                    data-testid="asset-insurance-expiry-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className={labelWithChip}>
                    <span>Sum Insured (£)</span>
                    {aiSuggestedFields.insured_value && <AiSuggestedChip />}
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.insured_value}
                    onChange={(e) => handleChange('insured_value', e.target.value)}
                    placeholder="e.g. 40000"
                    className={`luxury-input ${aiSuggestedFields.insured_value ? 'border-purple-500/50' : ''}`}
                    data-testid="asset-insured-value-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    The amount your policy will pay out. Leave blank if uninsured.
                  </p>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-4">
              <h2 className="text-gold uppercase tracking-wider text-xs font-semibold">Additional Notes</h2>
              
              <Textarea
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Any additional information..."
                className="luxury-input min-h-[100px]"
                data-testid="asset-notes-input"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                className="btn-primary flex-1"
                disabled={loading}
                data-testid="asset-submit-btn"
              >
                {loading ? 'Saving...' : isEditing ? 'Update Asset' : 'Add Asset'}
              </Button>
              <Link to="/assets" className="flex-1">
                <Button type="button" className="btn-secondary w-full" data-testid="asset-cancel-btn">
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
